import FormicaInput from 'components/custom/FormicaInput';
import React, { ChangeEvent, Component } from 'react';
import AiModelTrainingParameterComponent from './AiModelTrainingParameterComponent';
import { RootState } from '../../../redux/store';
import { AiModelState } from '../../../redux/reducers/AiModelReducer';
import { changeAiModel, changeAiModelIsUpdate, clearAiModel } from '../../../redux/actions/AiModelActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { clearPage } from '../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import RoleHelper from '../../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import FormicaMultiSelectBox from '../../custom/FormicaMultiSelectBox';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import ComponentName from '../../../constant/ComponentName';
import DataType from '../../../constant/DataType';
import SubscriptionContext = subscribe.SubscriptionContext;
import QueryableRepository = repository.QueryableRepository;
import CollectionNames from '../../../constant/CollectionNames';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import CustomDivider from '../../custom/CustomDivider';
import LanguageUtil from '../../../util/LanguageUtil';

class AiModelTrainingValidationComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);
  private readonly repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private computation: subscribe.Computation | undefined;

  public render() {
    const {
      aiModel: { aiModel, isUpdate },
    } = this.props;
    const { loading } = this.state;
    const sortMetricOptions = this.getSortMetricOptions();
    const stoppingMetricOptions = this.getStoppingMetric();
    const algorithmOptions = this.getAlgorithmOptions();

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div
        className={
          'w-full h-full flex flex-col p-[20px] gap-[15px] pt-[30px] scrollable-editor-container overflow-scroll max-h-[calc(100vh-18.75rem)] pb-[30px]'
        }
      >
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.randomSeed.label', defaultMessage: 'Random Seed' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.randomSeed.description',
            defaultMessage: 'A random seed is a number used to initialize a pseudorandom number generator.',
          })}
          input={
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.randomSeed', defaultMessage: 'Random Seed' })}
              value={aiModel.parameters.seed}
              type='number'
              inputMode='numeric'
              onChange={this.onChangeRandomSeed}
            />
          }
        />
        <CustomDivider />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.samplingRatio.label', defaultMessage: 'Sampling Ratio' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.samplingRatio.description',
            defaultMessage: 'Determines the ratio of the target classes in the training dataset.',
          })}
          input={
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.samplingRatio', defaultMessage: 'Sampling Ratio' })}
              value={aiModel.samplingRatio}
              type='number'
              inputMode='numeric'
              onChange={this.onChangeSamplingRatio}
            />
          }
        />
        <CustomDivider />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.algorithmModel.label', defaultMessage: 'Algorithm Model' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.algorithmModel.description',
            defaultMessage: 'raining uses various machine learning algorithms. If multiple models are chosen, the best one will be picked after training.',
          })}
          input={
            <FormicaMultiSelectBox
              selectedItems={aiModel.algorithms.map((a) => {
                const label = algorithmOptions.find((m) => m.value == a)?.label;
                return {
                  label: label ?? a,
                  value: a,
                };
              })}
              maxTagSize={algorithmOptions.length}
              onChange={this.onChangeAlgorithms}
              options={algorithmOptions}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.algorithmModel', defaultMessage: 'Algorithm Model' })}
            />
          }
        />
        <CustomDivider />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.maxModels.label', defaultMessage: 'Max Models' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.maxModels.description',
            defaultMessage: 'Use this option to specify the maximum number of models to build in the AutoML run, excluding the Stacked Ensemble models.',
          })}
          input={
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.maxModels', defaultMessage: 'Max Models' })}
              value={aiModel.parameters.maxModels}
              type='number'
              inputMode='numeric'
              onChange={this.onChangeMaxModels}
            />
          }
        />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.maxRuntime.label', defaultMessage: 'Max Runtime' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.maxRuntime.description',
            defaultMessage: 'When building a model, this option specifes the maximum runtime in seconds that you want to allot in order to complete the model.',
          })}
          input={
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.maxRuntime', defaultMessage: 'Max Runtime' })}
              value={aiModel.parameters.maxRuntimeInMinutes}
              type='number'
              inputMode='numeric'
              min={10}
              onChange={this.onChangeMaxRuntime}
            />
          }
        />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.sortMetric.label', defaultMessage: 'Sort Metric' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.sortMetric.description',
            defaultMessage: 'Specifies the type of metric against which model performance is benchmarked.',
          })}
          input={
            <FormicaSearchSelectBox
              value={aiModel.parameters.sortMetric}
              onChange={this.onChangeSortMetric}
              options={sortMetricOptions}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.sortMetric', defaultMessage: 'Sort Metric' })}
            />
          }
        />
        <AiModelTrainingParameterComponent
          title={LanguageUtil.get({ id: 'pages.ai.editor.training.fields.stoppingMetric.label', defaultMessage: 'Stopping Metric' })}
          description={LanguageUtil.get({
            id: 'pages.ai.editor.training.fields.stoppingMetric.description',
            defaultMessage: 'This option specifies the metric to consider when early stopping is specified.',
          })}
          input={
            <FormicaSearchSelectBox
              rounded
              disabled={!RoleHelper.canEdit(ComponentName.AI_MODEL) || isUpdate}
              options={stoppingMetricOptions}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.stoppingMetric', defaultMessage: 'Stopping Metric' })}
              value={aiModel.parameters.stoppingMetric}
              onChange={this.onChangeStoppingMetric}
            />
          }
        />
      </div>
    );
  }

  public async componentDidMount() {
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const transactionSubscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    this.computation = subscribe.Tracker.autorun(this.tracker, transactionSubscriptions);
  };

  private tracker = async () => {
    await this.buildData();
  };

  private stopTracker = () => {
    this.computation?.stop();
  };

  private buildData = async () => {
    const {
      aiModel: { aiModel },
    } = this.props;
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', aiModel.channel)).build();
    const transaction: TransactionJsonModel | undefined = await this.repository.find(transactionQuery);
    this.setState({ transaction, loading: false });
  };

  private onChangeRandomSeed = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.parameters.seed = this.parseNumberValue(e);
    changeAiModel(aiModel);
  };

  private onChangeStoppingMetric = (e: FormicaSelectBoxItem) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.parameters.stoppingMetric = e.value;
    changeAiModel(aiModel);
  };

  private onChangeSamplingRatio = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.samplingRatio = this.parseNumberValue(e);
    changeAiModel(aiModel);
  };

  private onChangeAlgorithms = (e: FormicaSelectBoxItem[]) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.algorithms = e.map((a) => a.value);
    changeAiModel(aiModel);
  };

  private onChangeMaxModels = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.parameters.maxModels = this.parseNumberValue(e);
    changeAiModel(aiModel);
  };

  private onChangeMaxRuntime = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.parameters.maxRuntimeInMinutes = this.parseNumberValue(e);
    changeAiModel(aiModel);
  };

  private onChangeSortMetric = (e: FormicaSelectBoxItem) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    aiModel.parameters.sortMetric = e.value;
    changeAiModel(aiModel);
  };

  private getStoppingMetric() {
    return [
      {
        label: 'Auto',
        value: 'Auto',
      },
      {
        label: 'Log Loss',
        value: 'logloss',
      },
      {
        label: 'AUC (Area under the ROC curve)',
        value: 'auc',
      },
      {
        label: 'AUCPR (Area under the Precision-Recall curve)',
        value: 'aucpr',
      },
      {
        label: 'Misclassification',
        value: 'misclassification',
      },
      {
        label: 'Mean per class error',
        value: 'mpce',
      },
    ];
  }

  private parseNumberValue = (e: ChangeEvent<HTMLInputElement>) => {
    let value: number | undefined = undefined;
    try {
      value = parseInt(e.target.value);
      if (isNaN(value)) {
        value = undefined;
      }
    } catch (e) {
      value = undefined;
    }
    return value;
  };

  private getAlgorithmOptions(): FormicaSelectBoxItem[] {
    return [
      {
        label: 'GLM (Generalized Linear Model',
        value: 'glm',
      },
      {
        label: 'DRF (Distributed Random Forest)',
        value: 'drf',
      },
      {
        label: 'XGBoost',
        value: 'xgboost',
      },
      {
        label: 'GBM (Gradient Boosting Machine)',
        value: 'gbm',
      },
      {
        label: 'Deep Learning',
        value: 'deepLearning',
      },
      {
        label: 'Stacked Ensemble (edited)',
        value: 'stackedEnsemble',
      },
    ];
  }

  private getSortMetricOptions() {
    return [
      {
        label: 'Auto',
        value: 'Auto',
      },
      {
        label: 'Log Loss',
        value: 'logloss',
      },
      {
        label: 'AUC (Area under the ROC curve)',
        value: 'auc',
      },
      {
        label: 'AUCPR (Area under the Precision-Recall curve)',
        value: 'aucpr',
      },
      {
        label: 'Mean per class error',
        value: 'mpce',
      },
    ];
  }

  private getDateTimeFieldOptions = (): FormicaSelectBoxItem[] => {
    const { transaction } = this.state;
    const {
      aiModel: { aiModel },
    } = this.props;
    if (transaction == undefined) return [];
    return aiModel.baseFeatures
      .filter((v) => v.properties.dataType == DataType.DATE)
      .map((v) => ({
        label: v.name,
        value: v.name,
      }));
  };
}

class State {
  transaction: TransactionJsonModel | undefined;
  loading: boolean = true;
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  isUpdate: state.aiModel.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeAiModelIsUpdate,
  changeAiModel,
  clearAiModel,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(AiModelTrainingValidationComponent));
